import { useMemo, useState } from 'react';
import departements from '@/data/departements.json';
import { geometryToPath, projectedBounds, VIEWBOX } from '@/lib/mapProjection';

/**
 * Choropleth map of France departments.
 * Props:
 *  - counts: { [deptCode: string]: number }  how many events per dept
 *  - selectedDept: currently selected dept code (string) or ''
 *  - onSelectDept(code, name): called when a department is clicked
 */
export default function FranceChoroplethMap({ counts, selectedDept, onSelectDept }) {
  const [hovered, setHovered] = useState(null);

  const maxCount = useMemo(() => {
    const vals = Object.values(counts || {});
    return vals.length ? Math.max(...vals, 1) : 1;
  }, [counts]);

  // Color scale: #1A1A1A (no events) -> #FFC107 amber (many events)
  // We keep it visually calm but punchy on hover.
  const colorFor = (code) => {
    const n = counts?.[code] || 0;
    if (n === 0) return '#1C1C1C';
    const t = Math.min(n / Math.max(maxCount, 1), 1);
    // Interpolate #2A1F00 -> #FFC107
    const r = Math.round(42 + (255 - 42) * t);
    const g = Math.round(31 + (193 - 31) * t);
    const b = Math.round(0 + (7 - 0) * t);
    return `rgb(${r},${g},${b})`;
  };

  const paths = useMemo(() => {
    if (!departements || !departements.features) return [];
    return departements.features.map((f) => {
      const code = f.properties?.code || '';
      const nom = f.properties?.nom || '';
      const d = geometryToPath(f.geometry);
      const b = projectedBounds(f.geometry);
      const cx = (b.minX + b.maxX) / 2;
      const cy = (b.minY + b.maxY) / 2;
      return { code, nom, d, cx, cy };
    });
  }, []);

  return (
    <div className="relative w-full">
      <svg
        viewBox={VIEWBOX}
        className="w-full h-auto select-none"
        style={{ maxHeight: '72vh' }}
        data-testid="france-choropleth-svg"
      >
        <defs>
          <filter id="glow-amber" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {paths.map((p) => {
          const isSelected = selectedDept === p.code;
          const isHovered = hovered?.code === p.code;
          const n = counts?.[p.code] || 0;
          const baseFill = colorFor(p.code);
          return (
            <path
              key={p.code}
              d={p.d}
              fill={isHovered ? '#FF6B35' : baseFill}
              fillOpacity={isHovered ? 0.95 : 1}
              stroke={isSelected ? '#FF6B35' : isHovered ? '#FFFFFF' : 'rgba(255,255,255,0.08)'}
              strokeWidth={isSelected ? 2.5 : isHovered ? 1.4 : 0.6}
              style={{
                cursor: 'pointer',
                transition: 'fill 180ms ease, stroke 180ms ease, filter 180ms ease, fill-opacity 180ms ease',
                filter: isSelected ? 'url(#glow-amber)' : isHovered ? 'drop-shadow(0 0 6px rgba(255,107,53,0.8))' : 'none',
              }}
              onMouseEnter={() => setHovered({ code: p.code, nom: p.nom, count: n })}
              onMouseLeave={() => setHovered(null)}
              onClick={() => onSelectDept?.(p.code, p.nom)}
              data-testid={`france-dept-${p.code}`}
            />
          );
        })}

        {/* Event count label ONLY for departments with events */}
        {paths
          .filter((p) => (counts?.[p.code] || 0) > 0)
          .map((p) => (
            <g key={`lbl-${p.code}`} style={{ pointerEvents: 'none' }}>
              <circle cx={p.cx} cy={p.cy} r={10} fill="rgba(0,0,0,0.55)" />
              <text
                x={p.cx}
                y={p.cy + 3}
                textAnchor="middle"
                fontSize="11"
                fontWeight="700"
                fill="#FFC107"
                fontFamily="Manrope, sans-serif"
              >
                {counts[p.code]}
              </text>
            </g>
          ))}
      </svg>

      {/* Hover tooltip */}
      {hovered && (
        <div
          className="pointer-events-none absolute top-3 left-3 px-3 py-1.5 rounded-lg bg-black/85 backdrop-blur border border-white/10 text-xs"
          data-testid="france-hover-tooltip"
        >
          <span className="text-white font-semibold">{hovered.nom}</span>
          <span className="text-[#FFC107] ml-2 font-bold">
            {hovered.count} évènement{hovered.count > 1 ? 's' : ''}
          </span>
        </div>
      )}
    </div>
  );
}
